import React from 'react';
import { Box, Typography, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  GlassHeroContainer,
} from '../../components/glassmorphism/GlassmorphismStyles';
import GetStarted from '../../components/GetStarted/GetStarted';

const CookiesPage: React.FC = () => {
  return (
    <GlassHeroContainer>
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: { xs: 14, sm: 20 },
          pb: { xs: 8, sm: 12 },
        }}
      >
        <CookiesContainer>
          {/* Header Section */}
          <HeaderSection>
            <HeadingWrapper>
              <MainHeading>Cookies Policy</MainHeading>
              <LastUpdated><strong>Last updated: November 27, 2025</strong></LastUpdated>
            </HeadingWrapper>
          </HeaderSection>

          {/* Section 1: Introduction */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>1. Introduction</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                Welcome to Womapp ("we," "our," or "us"). This Cookies Policy explains what cookies are, how we use them on our website <StyledLink href="https://womapp.uk/" target="_blank" rel="noopener noreferrer">https://womapp.uk/</StyledLink>, and your rights to control them.
                <br /><br />
                This policy applies to all visitors to our website. We are committed to protecting your privacy in compliance with the UK General Data Protection Regulation (UK GDPR) and the Privacy and Electronic Communications Regulations (PECR).
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 2: What are Cookies? */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>2. What are Cookies?</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                Cookies are small text files placed on your device (computer, tablet, or mobile) when you visit a website. They allow the website to recognize your device, store information about your preferences, and improve your experience.
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 3: Your Consent and Lawful Basis */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>3. Your Consent and Lawful Basis</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                We categorize cookies into "Strictly Necessary" and "Non-Essential" (e.g., Analytics, Functionality).
                <br /><br />
                <strong>Strictly Necessary Cookies:</strong> We do not require your consent to place these cookies, as they are essential for the website to function (e.g., for security, payment processing, or load balancing). Our lawful basis for using these is our legitimate interest in providing you with a secure and functioning website.
                <br /><br />
                <strong>Non-Essential Cookies:</strong> We will only set analytics, marketing, or non-essential functionality cookies on your device if you have given us your explicit consent. You have the right to withdraw this consent at any time.
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 4: How We Use Cookies */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>4. How We Use Cookies</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                We use cookies for the following purposes:
                <br /><br />
                <strong>Strictly Necessary:</strong> To enable core functionality such as security, network management, and payment processing. You cannot disable these.
                <br /><br />
                <strong>Functionality:</strong> To remember choices you make (such as your theme preference or chat history) to provide a more personalized experience.
                <br /><br />
                <strong>Analytics & Performance:</strong> To help us understand how visitors interact with our website by collecting and reporting information anonymously.
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 5: Managing Your Cookie Preferences */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>5. Managing Your Cookie Preferences</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                You have full control over your cookie settings.
                <br /><br />
                <strong>Consent Banner:</strong> Upon your first visit, a banner will appear asking for your consent. No non-essential cookies will be set unless you click "Accept".
                <br /><br />
                <strong>Browser Settings:</strong> Alternatively, you can block cookies via your browser settings. Please note that blocking strictly necessary cookies may cause parts of our website (such as payments or logging in) to stop working correctly.
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 6: Details of Cookies We Use */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>6. Details of Cookies We Use</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                Below is a detailed list of the cookies used on our website.
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 6.1: Strictly Necessary Cookies */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>6.1. Strictly Necessary Cookies</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                These cookies are essential for the website to secure connections, process payments, and manage network traffic.
              </BodyText>
              <CookieTable>
                <thead>
                  <tr>
                    <th>Cookie Name</th>
                    <th>Provider</th>
                    <th>Purpose</th>
                    <th>Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>__stripe_mid</td>
                    <td>Stripe</td>
                    <td>Used for fraud detection and payment security.</td>
                    <td>1 year</td>
                  </tr>
                  <tr>
                    <td>__stripe_sid</td>
                    <td>Stripe</td>
                    <td>Used for fraud detection and payment security.</td>
                    <td>30 minutes</td>
                  </tr>
                  <tr>
                    <td>m</td>
                    <td>Stripe</td>
                    <td>Determines the device used to access the website for fraud prevention.</td>
                    <td>~1 year</td>
                  </tr>
                  <tr>
                    <td>__cf_bm</td>
                    <td>Cloudflare</td>
                    <td>Determines the device used to access the website for fraud prevention.</td>
                    <td>~1 year</td>
                  </tr>
                  <tr>
                    <td>__cfruid</td>
                    <td>Cloudflare</td>
                    <td>Used for rate limiting and network session consistency.</td>
                    <td>Session</td>
                  </tr>
                  <tr>
                    <td>AWSALBCORS</td>
                    <td>Amazon Web Services</td>
                    <td>Used for load balancing to ensure the website runs smoothly.</td>
                    <td>7 days</td>
                  </tr>
                  <tr>
                    <td>session_id</td>
                    <td>Womapp</td>
                    <td>Maintains your active user session.</td>
                    <td>Session</td>
                  </tr>
                </tbody>
              </CookieTable>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 6.2: Functionality Cookies */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>6.2. Functionality Cookies</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                These cookies enable enhanced functionality and personalization, such as live chat support and visual themes.
              </BodyText>
              <CookieTable>
                <thead>
                  <tr>
                    <th>Cookie Name</th>
                    <th>Provider</th>
                    <th>Purpose</th>
                    <th>Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>__zlcstore</td>
                    <td>Zendesk</td>
                    <td>Stores the status of the live chat widget.</td>
                    <td>Persistent</td>
                  </tr>
                  <tr>
                    <td>ZD-store</td>
                    <td>Stripe</td>
                    <td>Stores user settings for the support chat.</td>
                    <td>Persistent</td>
                  </tr>
                  <tr>
                    <td>ZD-suid</td>
                    <td>Zendesk</td>
                    <td>Unique ID for the user in the support chat session.</td>
                    <td>Persistent</td>
                  </tr>
                  <tr>
                    <td>map-mode</td>
                    <td>Womapp</td>
                    <td>Remembers your preferred theme (light or dark mode).</td>
                    <td>Persistent</td>
                  </tr>
                  <tr>
                    <td>map-color-scheme-*</td>
                    <td>Womapp</td>
                    <td>Stores specific color scheme preferences for the interface.</td>
                    <td>Persistent</td>
                  </tr>
                </tbody>
              </CookieTable>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 6.3: Analytics Cookies */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>6.3. Analytics Cookies</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                These cookies help us improve our website by analyzing how you use it. <strong>These are not loaded until you give consent.</strong>
              </BodyText>
              <CookieTable>
                <thead>
                  <tr>
                    <th>Cookie Name</th>
                    <th>Provider</th>
                    <th>Purpose</th>
                    <th>Expiry</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>_ga</td>
                    <td>Google Analytics</td>
                    <td>Used to distinguish users and calculate visitor data.</td>
                    <td>2 years</td>
                  </tr>
                  <tr>
                    <td>_gid</td>
                    <td>Google Analytics</td>
                    <td>Used to distinguish users.</td>
                    <td>24 hours</td>
                  </tr>
                </tbody>
              </CookieTable>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 7: Third-Party Cookies */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>7. Third-Party Cookies</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                Some cookies are placed by third-party services that appear on our pages (e.g., Stripe for payments, Zendesk for support). We do not control these cookies directly. We recommend you check the privacy policies of these third parties for more information about their cookies.
                <br /><br />
                <strong>Non-essential third-party cookies (such as Google Analytics, Facebook, or Mixpanel) are only loaded after you have given consent via our cookie banner.</strong>
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>

          {/* Section 8: Contact Us */}
          <ContentSection>
            <SectionTitleWrapper>
              <SectionTitle>8. Contact Us</SectionTitle>
            </SectionTitleWrapper>
            <SectionContentWrapper>
              <BodyText>
                If you have any questions about this Cookies Policy or how we handle your data, please contact us at:
                <br /><br />
                <strong>Email:</strong> <StyledLink href="mailto:support@womapp.uk">support@womapp.uk</StyledLink>
                <br />
                <strong>Post:</strong> Word of Mouth LTD, Cambridge House, Highgate, Forest Row, East Sussex, United Kingdom, RH18 5BA
              </BodyText>
            </SectionContentWrapper>
          </ContentSection>
        </CookiesContainer>

        {/* Get Started CTA */}
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', mt: 8, mb: 6 }}>
          <GetStarted />
        </Box>
      </Container>
    </GlassHeroContainer>
  );
};

// Main container
const CookiesContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: '0px',
  gap: '24px',
  width: '100%',
  maxWidth: '1150px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    padding: '0 20px',
  },
}));

// Header Section
const HeaderSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '48px',
  width: '100%',
  maxWidth: '1150px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Heading Wrapper
const HeadingWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '16px',
  width: '100%',
  maxWidth: '760px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Main Heading
const MainHeading = styled(Typography)(({ theme }) => ({
  width: '100%',
  maxWidth: '760px',
  fontFamily: 'Montserrat',
  fontStyle: 'normal',
  fontWeight: 500,
  fontSize: '60px',
  lineHeight: '120%',
  textAlign: 'center',
  letterSpacing: '-0.5px',
  color: theme.palette.mode === 'dark' ? '#00FFFF' : '#005CAD',

  [theme.breakpoints.down('md')]: {
    fontSize: '48px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '36px',
  },
}));

// Last Updated
const LastUpdated = styled(Typography)(({ theme }) => ({
  margin: '0 auto',
  fontFamily: 'Roboto',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '175%',
  textAlign: 'center',
  letterSpacing: '0.15px',
  color: theme.palette.mode === 'dark' ? '#B6B6B6' : '#1A1A1A',

  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',
  },
}));

// Content Section
const ContentSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '8px',
  width: '100%',
  maxWidth: '1150px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Section Title Wrapper
const SectionTitleWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '16px',
  width: '100%',
  maxWidth: '760px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Section Title
const SectionTitle = styled(Typography)(({ theme }) => ({
  width: '100%',
  fontFamily: 'Roboto',
  fontStyle: 'normal',
  fontWeight: 700,
  fontSize: '16px',
  lineHeight: '175%',
  letterSpacing: '0.15px',
  color: theme.palette.mode === 'dark' ? '#00FFFF' : '#005CAD',
  textAlign: 'left',
}));

// Section Content Wrapper
const SectionContentWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '0px',
  gap: '16px',
  width: '100%',
  maxWidth: '760px',

  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

// Body Text
const BodyText = styled(Typography)(({ theme }) => ({
  width: '100%',
  fontFamily: 'Roboto',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '150%',
  letterSpacing: '0.15px',
  color: theme.palette.mode === 'dark' ? '#B6B6B6' : '#1A1A1A',
  textAlign: 'left',
}));

// Simple HTML table styling
const CookieTable = styled('table')(({ theme }) => ({
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: '16px',
  fontFamily: 'Roboto',

  '& thead': {
    backgroundColor: theme.palette.mode === 'dark' ? '#2A2A2A' : '#D1D0C6',
  },

  '& th': {
    padding: '8px 24px',
    textAlign: 'left',
    fontWeight: 700,
    fontSize: '16px',
    lineHeight: '175%',
    letterSpacing: '0.15px',
    color: theme.palette.mode === 'dark' ? '#E5E5E5' : '#1A1A1A',
    border: '1px solid #393939',
  },

  '& td': {
    padding: '24px 16px',
    fontSize: '16px',
    lineHeight: '150%',
    letterSpacing: '0.15px',
    color: theme.palette.mode === 'dark' ? '#B6B6B6' : '#393939',
    border: '1px solid #393939',
    backgroundColor: theme.palette.mode === 'dark' ? '#1A1A1A' : '#E5E5DC',
  },

  '& tbody tr:nth-of-type(odd) td': {
    backgroundColor: theme.palette.mode === 'dark' ? '#1A1A1A' : '#E5E5DC',
  },

  [theme.breakpoints.down('sm')]: {
    fontSize: '14px',

    '& th, & td': {
      padding: '8px 12px',
      fontSize: '14px',
    },
  },
}));

// Styled Link for clickable links
const StyledLink = styled('a')(({ theme }) => ({
  fontFamily: 'Roboto',
  fontStyle: 'normal',
  fontWeight: 400,
  fontSize: '16px',
  lineHeight: '150%',
  letterSpacing: '0.15px',
  color: theme.palette.mode === 'dark' ? '#00FFFF' : '#005CAD',
  textDecoration: 'none',
  cursor: 'pointer',

  '&:hover': {
    textDecoration: 'underline',
  },
}));

export default CookiesPage;
